import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { router } from '@inertiajs/react'
import { Trash2, CheckCircle, Search, ArrowUpDown, ChevronLeft, ChevronRight, X, UserPlus, CalendarCheck, CalendarOff, Settings } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useState } from 'react'

interface User {
  id: number
  name: string
  email: string
  phone: string
  role: string
  verified: boolean
  bookable: boolean
  created_at: string
}

interface School {
  id: number
  name: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  verified?: string
  search?: string
  sort_by: string
  sort_direction: string
}

interface TeamIndexProps {
  users: User[]
  school: School
  pagination: Pagination
  filters: Filters
}

export default function TeamIndex({ users, school, pagination, filters }: TeamIndexProps) {
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null)
  const [deleteUserEmail, setDeleteUserEmail] = useState<string>('')
  const [verifyUserId, setVerifyUserId] = useState<number | null>(null)
  const [verifyUserName, setVerifyUserName] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState(filters.search || '')

  const handleFilter = (key: string, value: string) => {
    router.get('/team', {
      ...filters,
      [key]: value,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/team', {
      ...filters,
      search: searchTerm,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const clearSearch = () => {
    setSearchTerm('')
    router.get('/team', {
      ...filters,
      search: '',
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleSort = (column: string) => {
    const newDirection = filters.sort_by === column && filters.sort_direction === 'asc' ? 'desc' : 'asc'
    router.get('/team', {
      ...filters,
      sort_by: column,
      sort_direction: newDirection
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handlePageChange = (page: number) => {
    router.get('/team', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleDelete = (id: number) => {
    router.delete(`/users/${id}`, {
      onSuccess: () => {
        setDeleteUserId(null)
        setDeleteUserEmail('')
      }
    })
  }

  const handleVerify = (id: number) => {
    router.patch(`/users/${id}/verify`, {}, {
      onSuccess: () => {
        setVerifyUserId(null)
        setVerifyUserName('')
      }
    })
  }

  const getRoleBadge = (role: string) => {
    const variants = {
      teacher: 'default',
      manager: 'secondary'
    }
    const labels = {
      teacher: 'Professor',
      manager: 'Gerente'
    }
    return <Badge variant={variants[role as keyof typeof variants] as any}>{labels[role as keyof typeof labels]}</Badge>
  }

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Equipe</h2>
            <p className="text-sm text-muted-foreground">
              Gerencie professores e gerentes da escola
            </p>
          </div>
          <Button onClick={() => router.visit('/users/new?context=team')}>
            <UserPlus className="h-4 w-4 mr-2" />
            Novo Membro
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Membros da Equipe</CardTitle>
            <CardDescription>
              {pagination.total_count} {pagination.total_count === 1 ? 'membro' : 'membros'} na equipe
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filtros */}
            <div className="flex gap-4 flex-wrap">
              <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                <Button type="submit">Buscar</Button>
              </form>

              <Select
                value={filters.verified || 'all'}
                onValueChange={(value) => handleFilter('verified', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="true">Verificados</SelectItem>
                  <SelectItem value="false">Não Verificados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tabela */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 p-0 h-auto font-semibold hover:bg-transparent"
                      >
                        Nome
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-1 p-0 h-auto font-semibold hover:bg-transparent"
                      >
                        Email
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('role')}
                        className="flex items-center gap-1 p-0 h-auto font-semibold hover:bg-transparent"
                      >
                        Cargo
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('verified')}
                        className="flex items-center gap-1 p-0 h-auto font-semibold hover:bg-transparent"
                      >
                        Status
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>Agendamento</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhum membro encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone}</TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell>
                          {user.verified ? (
                            <Badge variant="default" className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Verificado
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Pendente</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.role === 'teacher' ? (
                            user.bookable ? (
                              <Badge variant="default" className="bg-green-500">
                                <CalendarCheck className="h-3 w-3 mr-1" />
                                Aberto
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <CalendarOff className="h-3 w-3 mr-1" />
                                Só Admin
                              </Badge>
                            )
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {!user.verified && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setVerifyUserId(user.id)
                                    setVerifyUserName(user.name)
                                  }}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Verificar Membro</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja verificar <strong>{verifyUserName}</strong>?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel onClick={() => {
                                    setVerifyUserId(null)
                                    setVerifyUserName('')
                                  }}>
                                    Cancelar
                                  </AlertDialogCancel>
                                  <AlertDialogAction onClick={() => verifyUserId && handleVerify(verifyUserId)}>
                                    Verificar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => router.visit(`/users/${user.id}`)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Configurar
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setDeleteUserId(user.id)
                                  setDeleteUserEmail(user.email)
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir <strong>{deleteUserEmail}</strong>?
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => {
                                  setDeleteUserId(null)
                                  setDeleteUserEmail('')
                                }}>
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteUserId && handleDelete(deleteUserId)}>
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Paginação */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Página {pagination.current_page} de {pagination.total_pages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.total_pages}
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
